import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
  Typography,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import DocxViewer from "../../../../components/DocxViewer/DocxViewer";
import XlsxViewer from "../../../../components/XlsxViewer/XlsxViewer";
import TextViewer from "../../../../components/TextViewer/TextViewer";
import { formatDate } from "../../../../utils/dateUtils";

const FilePreviewDialog = ({ open, onClose, file, exception }) => {
  if (!file) return null;

  // Build file URL
  const fileUrl = `${process.env.REACT_APP_CLOUDFRONT_URL}/${file.path}`;
  const filePath = file.path?.toLowerCase() || "";

  // Detect file types
  const isPdf = filePath.endsWith(".pdf");
  const isDocx = filePath.endsWith(".docx");
  const isXlsx = /\.(xlsx|xls)$/i.test(filePath);
  const isText = filePath.endsWith(".txt");
  const isImage = /\.(jpg|jpeg|png|gif)$/i.test(filePath);
  // .doc (legacy binary) and .zip have no in-browser viewer → download-only.
  const isUnsupported = !isPdf && !isDocx && !isXlsx && !isText && !isImage;

  const handleDownload = async () => {
    if (file.path) {
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = file.maskName || "evidence-file";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Error downloading file:", error);
        window.open(fileUrl, "_blank");
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "85vh",
          borderRadius: "12px",
          maxWidth: "1100px",
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: "1px solid #E5E7EB",
          fontWeight: 600,
          fontSize: "18px",
          py: 2,
          px: 2.5,
          backgroundColor: "#F7F7FC",
        }}
      >
        {file.maskName || "Evidence File"}
        <Tooltip title="Download File">
          <IconButton
            onClick={handleDownload}
            sx={{
              position: "absolute",
              right: 56,
              top: 12,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
        <Grid container sx={{ height: "100%" }}>
          {/* Left: Metadata */}
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              p: 2.5,
              borderRight: "1px solid #E5E7EB",
              overflowY: "auto",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  fontSize: "10px",
                }}
              >
                File Name
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  fontWeight: 500,
                  fontSize: "14px",
                  wordBreak: "break-word",
                }}
              >
                {file.maskName || "N/A"}
              </Typography>
            </Box>

            {exception?.reason && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    fontSize: "10px",
                  }}
                >
                  Justification
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "13px",
                    wordBreak: "break-word",
                  }}
                >
                  {exception.reason}
                </Typography>
              </Box>
            )}

            {exception?.exception_valid_till && (
              <Box sx={{ mb: 2  }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    fontSize: "10px",
                  }}
                >
                  Valid Until
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "13px",
                  }}
                >
                  {formatDate(exception.exception_valid_till)}
                </Typography>
              </Box>
            )}

            {exception?.created_at && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    fontSize: "10px",
                  }}
                >
                  Uploaded On
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: "text.secondary",
                    fontSize: "13px",
                  }}
                >
                  {formatDate(exception.created_at)}
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Right: Document Viewer */}
          <Grid
            item
            xs={12}
            md={9}
            sx={{
              height: "100%",
              overflow: "hidden",
              bgcolor: "#F3F4F6",
              p: 1,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #E5E7EB",
                backgroundColor: "#FFFFFF",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isPdf ? (
                <PdfViewer fileUrl={fileUrl} fillHeight />
              ) : isDocx ? (
                <DocxViewer fileUrl={fileUrl} fillHeight />
              ) : isXlsx ? (
                <XlsxViewer fileUrl={fileUrl} fillHeight />
              ) : isText ? (
                <TextViewer fileUrl={fileUrl} fillHeight />
              ) : isImage ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "auto",
                    p: 2,
                  }}
                >
                  <img
                    src={fileUrl}
                    alt={file.maskName || "Evidence"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
              ) : isUnsupported ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 4,
                    gap: 1.5,
                    textAlign: "center",
                  }}
                >
                  <InsertDriveFileOutlined
                    sx={{ fontSize: 56, color: "text.secondary" }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Preview not available
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 360 }}
                  >
                    In-browser preview isn&apos;t supported for this file type.
                    Download the file to view it.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{ mt: 1, textTransform: "none" }}
                  >
                    Download file
                  </Button>
                </Box>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewDialog;
